import { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export interface SharedLink {
  title: string;
  url: string;
  note: string;
}

interface Props {
  link: SharedLink | null;
  onClose(): void;
}

// Shows a one-time link (invitation, password reset) with a copy button.
const LinkModal: React.FC<Props> = ({ link, onClose }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => setCopied(false), [link]);

  const copy = async (input: HTMLInputElement | null) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
    } catch {
      input?.select();
    }
  };

  return (
    <Dialog open={link != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{link?.title}</DialogTitle>
          <DialogDescription>{link?.note}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input
            id="shared-link"
            readOnly
            value={link?.url ?? ""}
            aria-label="Link"
            className="font-mono text-xs"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button
            variant="outline"
            onClick={(e) => copy(e.currentTarget.parentElement?.querySelector("input") ?? null)}
          >
            {copied ? <Check /> : <Copy />}
            {copied ? "Gekopieerd" : "Kopiëren"}
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Klaar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkModal;
